/**
 * Layered-hair recolour + composite (Character Editor).
 *
 * The designer exports each hairstyle as a stack of GRAYSCALE layer PNGs
 * (transparent background, `<Style>_01.png` = bottom … `_0N.png` = top). The
 * editor recolours them in code so any hair colour is produced from one shape
 * set — no per-colour PNG folders.
 *
 * Recolour method (confirmed with the designer): each layer has no internal
 * hue variation, so we keep only its ALPHA (the shape + grainy texture) and
 * replace RGB with a computed shade. Source colour is irrelevant — black/grey
 * layers work as-is. Depth comes from stacking the layers, each painted with
 * its own shade (bottom = darkest → top = lightest highlight), mirroring how
 * skin uses a base→highlight ramp.
 *
 * Which shade a layer gets is decided by its `_NN` NAME, not by its position in the
 * stack — see {@link HairLayer.n}.
 */

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = encodeURI(url);
  });
}

/** One layer of a stack: where the PNG is, and which layer it CALLS ITSELF. */
export interface HairLayer {
  url: string;
  /**
   * The 1-based number in the filename's `_NN` suffix (`Beard_03.png` → 3), which is
   * what picks the shade — NOT the layer's position in the stack.
   *
   * Those two used to be the same thing, and a stack with a gap in its numbering
   * (`_01,_03,_04,_05`) then silently pulled every layer above the gap down one shade
   * and left the top shade unused. Keying on the name means a missing `_02` just
   * skips shade 2 and every other layer keeps the colour it is named for (Tamara,
   * 2026-08-04: "make it recognisable by layer name").
   */
  n: number;
}

/**
 * A fully TRANSPARENT PNG — what "none" pushes into a layered slot.
 *
 * A Rive image asset can't be emptied and the beard has no boolean to hide its shape,
 * so the only way to render no beard is to give the slot an image with nothing in it.
 * Not-pushing is NOT the same thing: the slot keeps whatever it last held (the `.riv`'s
 * own embedded beard on a fresh load), which is why the "—" tile used to leave the
 * beard on screen (Tamara, 2026-08-04: "beard also has an option for no beard").
 *
 * 8×8 rather than 1×1 — degenerate texture sizes are the kind of thing a renderer
 * treats specially, and the cost of eight rows of nothing is irrelevant.
 */
export async function buildBlankLayer(): Promise<Uint8Array | null> {
  const canvas = document.createElement('canvas');
  canvas.width = 8;
  canvas.height = 8;
  // A fresh canvas is already transparent black — nothing to draw.
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));
  if (!blob) return null;
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Recolour each layer to its shade (keeping alpha) and stack them into one PNG.
 * A layer named `_NN` takes `shades[NN - 1]`; a number past the end of the ramp
 * reuses the last shade. Returns the composited PNG bytes for `decodeImage`, or
 * null when there are no layers.
 */
export async function buildRecoloredHair(
  layers: HairLayer[],
  shades: string[],
): Promise<Uint8Array | null> {
  if (!layers.length) return null;
  const imgs = await Promise.all(layers.map((l) => loadImage(l.url)));
  const w = imgs[0].naturalWidth;
  const h = imgs[0].naturalHeight;
  if (!w || !h) return null;

  const main = document.createElement('canvas');
  main.width = w;
  main.height = h;
  const mctx = main.getContext('2d');
  const tmp = document.createElement('canvas');
  tmp.width = w;
  tmp.height = h;
  const tctx = tmp.getContext('2d');
  if (!mctx || !tctx) return null;

  imgs.forEach((img, i) => {
    // Shade by the layer's NAME (`_03` → shades[2]), clamped to the ramp's end.
    const shade = shades[Math.min(Math.max(layers[i].n - 1, 0), shades.length - 1)] ?? '#FFFFFF';
    // Draw the layer, then paint its silhouette: `source-in` fills only where
    // the layer has alpha, replacing RGB but preserving the alpha (grain).
    tctx.clearRect(0, 0, w, h);
    tctx.globalCompositeOperation = 'source-over';
    tctx.drawImage(img, 0, 0, w, h);
    tctx.globalCompositeOperation = 'source-in';
    tctx.fillStyle = shade;
    tctx.fillRect(0, 0, w, h);
    // Stack onto the main canvas (bottom → top).
    mctx.drawImage(tmp, 0, 0);
  });

  const blob = await new Promise<Blob | null>((res) => main.toBlob(res, 'image/png'));
  if (!blob) return null;
  return new Uint8Array(await blob.arrayBuffer());
}
